/**
 * Analytics utility for tracking key events in Tikt
 *
 * This provides a simple tracking interface that can be connected
 * to any analytics provider (GA4, Mixpanel, Amplitude, etc.)
 */

// Track when we're in development mode
const isDev = import.meta.env.DEV;

// Event names for consistency
export const EVENTS = {
  // Page views
  PAGE_VIEW: 'page_view',

  // Event discovery
  EVENT_VIEWED: 'event_viewed',
  EVENT_SHARED: 'event_shared',

  // Ticket selection
  TICKET_SELECTED: 'ticket_selected',
  TICKET_QUANTITY_CHANGED: 'ticket_quantity_changed',
  SEAT_SELECTED: 'seat_selected',

  // Checkout funnel
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
  CHECKOUT_ABANDONED: 'checkout_abandoned',

  // Organizer actions
  ORGANIZER_SIGNUP_STARTED: 'organizer_signup_started',
  ORGANIZER_SIGNUP_COMPLETED: 'organizer_signup_completed',
  EVENT_CREATED: 'event_created',
  EVENT_PUBLISHED: 'event_published',
};

/**
 * Track an analytics event
 * @param {string} eventName - The event name from EVENTS
 * @param {object} properties - Event properties/metadata
 */
export function track(eventName, properties = {}) {
  const eventData = {
    event: eventName,
    timestamp: new Date().toISOString(),
    ...properties,
  };

  // Log in development
  if (isDev) {
    console.log('[Analytics]', eventName, properties);
  }

  // Send to analytics provider
  // TODO: Replace with actual analytics integration
  // Example integrations:
  //
  // Google Analytics 4:
  // gtag('event', eventName, properties);
  //
  // Mixpanel:
  // mixpanel.track(eventName, properties);
  //
  // Amplitude:
  // amplitude.track(eventName, properties);
  //
  // PostHog:
  // posthog.capture(eventName, properties);

  // For now, store in sessionStorage for demo purposes
  try {
    const events = JSON.parse(sessionStorage.getItem('tikt_events') || '[]');
    events.push(eventData);
    sessionStorage.setItem('tikt_events', JSON.stringify(events.slice(-100))); // Keep last 100
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * Track a page view
 * @param {string} pageName - The page name
 * @param {object} properties - Additional properties
 */
export function trackPageView(pageName, properties = {}) {
  track(EVENTS.PAGE_VIEW, {
    page: pageName,
    url: window.location.pathname,
    referrer: document.referrer,
    ...properties,
  });
}

/**
 * Track event viewing (for organizer analytics)
 * @param {object} event - The event object
 */
export function trackEventViewed(event) {
  track(EVENTS.EVENT_VIEWED, {
    eventId: event.id,
    eventTitle: event.title,
    organizerId: event.organizerId,
    ticketTypes: event.tickets?.length || 0,
    isSeated: !!event.venueLayout?.type,
  });
}

/**
 * Track checkout start
 * @param {object} data - Checkout data
 */
export function trackCheckoutStarted(data) {
  track(EVENTS.CHECKOUT_STARTED, {
    eventId: data.eventId,
    ticketCount: data.tickets?.length || data.seats?.length || 0,
    total: data.total,
  });
}

/**
 * Track checkout completion
 * @param {object} order - The order object
 */
export function trackCheckoutCompleted(order) {
  track(EVENTS.CHECKOUT_COMPLETED, {
    orderId: order.id,
    eventId: order.eventId,
    total: order.total,
    ticketCount: order.tickets?.length || order.seats?.length || 0,
    paymentMethod: 'stripe', // For now
  });
}

/**
 * Get stored events (for demo/debugging)
 * @returns {array} Array of tracked events
 */
export function getStoredEvents() {
  try {
    return JSON.parse(sessionStorage.getItem('tikt_events') || '[]');
  } catch {
    return [];
  }
}

/**
 * Clear stored events
 */
export function clearStoredEvents() {
  sessionStorage.removeItem('tikt_events');
}

// Export a default object for convenience
export default {
  track,
  trackPageView,
  trackEventViewed,
  trackCheckoutStarted,
  trackCheckoutCompleted,
  EVENTS,
};
